import { Modal, ModalCloseButton, ModalContent } from '@chakra-ui/react';
import { Collapse } from '@chakra-ui/react';
import { StandardSnack } from '@sketch-note/design-system/src/components/snackbar';
import { ChevronDownIcon, CloseIcon, ErrorIcon } from '@sketchnote/icons';
import { Button } from '@sketchnote/shared-components';
import { TElement } from '@udecode/plate';
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { createEditor } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, Slate, withReact } from 'slate-react';
import { API } from 'src/api';
import { serialize } from 'src/utils/serialize';

import { useBoardStore } from '../../../features/boards/store/useBoardStore';
import { TaskFields } from '../../../features/tasks/components/TaskFields';
import { useTasksStore } from '../../../features/tasks/store/useTasksStore';
import { CustomFieldType, TaskT } from '../../../features/tasks/types/tasks';

export const TemplateEdit = ({
  item,
  setIsEditModalOpen,
  create,
}: {
  item?: Partial<TaskT>;
  setIsEditModalOpen: Dispatch<SetStateAction<boolean>>;
  create?: boolean;
}) => {
  const [detailsCollapse, setDetailsCollapse] = useState(true);
  const [name, setName] = useState(item?.name);
  const [fields, setFields] = useState(item?.fields);
  const [errorModalIsOpen, setErrorModalIsOpen] = useState(false);
  const boardStore = useBoardStore();
  const [didCreate, setDidCreate] = useState(create ?? undefined);

  /* shouldCreate is reference to create so that we can access the current value of create. 
  shouldCreate is undefined in case of Edit template*/
  const shouldCreate = useRef(create ?? undefined);

  useEffect(() => {
    /** fetch custom labels for task template, added isTaskTemplateId variable to let the BE know that
    this is task template call so data can be picked up from the right place.**/
    item?.id && boardStore.actions.fetchLabel({ id: item?.id, isTaskTemplateId: true });
    item?.id && boardStore.actions.fetchSelectables({ id: item?.id, isTaskTemplateId: true });

    if (typeof shouldCreate.current === 'boolean' && !shouldCreate.current) {
      setIsEditModalOpen(false);
    }

    return () => {
      if (shouldCreate.current) {
        deleteTemplate();
      }
    };
  }, [item, didCreate]);

  const setUnsetTemplateFields = (field: CustomFieldType, remove = false) => {
    if (remove) {
      setFields((prev) => {
        const res = prev?.filter((i) => field.fieldId !== i.fieldId);
        item && (item.fields = res);
        return res;
      });
    } else {
      setFields((prev) => {
        if (prev) {
          const retval = [...prev, field];
          item && (item.fields = retval);
          return retval;
        }
        item && (item.fields = [field]);
        return [field];
      });
    }
  };

  const editor = useMemo(() => withHistory(withReact(createEditor() as any)) as any, []);
  // const { onUpdateTemplate, unsetTemplate, tasks } = useTasksStore();

  const onUpdate = (templateName?: any) => {
    if ((templateName && serialize(templateName).length <= 0) || serialize(templateName) === '') {
      setErrorModalIsOpen(true);
      setIsEditModalOpen(true);
    } else {
      // const response = item?.id && onUpdateTemplate(item?.id, templateName);
      setIsEditModalOpen(false);
    }
  };

  const handleChangeTopic = (e: TElement[]) => {
    setName(e);
  };

  function getInitialValue(value): any[] {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string') {
      return [{ children: [{ text: value }] }];
    }
    return [{ children: [{ text: '' }] }];
  }
  const handleErrorModal = () => {
    setErrorModalIsOpen(false);
  };

  const deleteTemplate = async () => {
    try {
      const response = await API.delete(`/board/task/template?id=${item?.id}`);
      if (response && item?.id) {
        // unsetTemplate(item?.id);
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  return (
    <>
      <div className="flex items-center justify-between py-20 px-30 shadow-sm">
        <span className="text-gray-500 text-sm font-regular">Editing Template</span>
        <CloseIcon
          wrapperClass="text-gray-500"
          size={12}
          onClick={(e) => {
            if ((name && serialize(name).length <= 0) || serialize(name) === '') {
              deleteTemplate();
            } else {
              onUpdate(name);
            }
            setIsEditModalOpen(false);
          }}
        />
      </div>
      <div className="flex h-full w-full">
        <div className="mt-20">
          <div className="flex-grow px-64 pb-4 overflow-auto no-scrollbar h-575 w-768">
            <Slate editor={editor} value={getInitialValue(name)} onChange={handleChangeTopic}>
              <Editable
                placeholder={item?.name ? item?.name.toString() : 'Untitled'}
                id={item?.id}
                className="font-bold mb-20 text-2xl w-full rounded border-2 border-transparent focus:border-gray-100 focus:bg-gray-100 focus:px-5"
              />
            </Slate>
            <div className="flex flex-row mb-24 justify-between">
              <section className="flex">
                <div className="flex flex-col">
                  <h5 className="mb-6 text-gray-800 font-medium text-md">📇️ Task details</h5>
                  <p className="text-sm font-regular text-gray-600">
                    Creating a template will only save your field names and not the data you’ve
                    added.
                  </p>
                </div>
              </section>
              <section role="button" className="flex items-center justify-between">
                <span
                  style={{
                    transform: detailsCollapse ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'all 0.5s',
                  }}
                  className={'ml-20 mr-6 block text-gray-500'}
                  onClick={(e) => {
                    setDetailsCollapse(!detailsCollapse);
                  }}
                >
                  <ChevronDownIcon />
                </span>
              </section>
            </div>

            <section className="border-b">
              <TaskFields task={item} setUnsetTemplateFields={setUnsetTemplateFields} />
            </section>
          </div>
        </div>
      </div>
      <div className="py-10 px-30 shadow-sm">
        <Button
          status="primary"
          className="min-w-100 float-right"
          onClick={() => {
            create && (shouldCreate.current = false);
            onUpdate(name);
            setDidCreate(!didCreate);
          }}
        >
          {create ? 'Create template' : 'Update template'}
        </Button>
      </div>
      <Modal isOpen={errorModalIsOpen} onClose={handleErrorModal}>
        <ModalContent style={{ width: '40rem', borderRadius: '10px', marginTop: '65rem' }}>
          <StandardSnack icon={<ErrorIcon />} text="Please enter template name" />
        </ModalContent>
      </Modal>
    </>
  );
};
